import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchTeamRankHistory,
  type BETeamRankHistoryGameweek,
  type TeamRankHistoryResponse,
} from '@/lib/network';

export const TEAM_RANK_HISTORY_QUERY_KEY_PREFIX = ['teams', 'rank-history'] as const;
const TEAM_RANK_HISTORY_STALE_TIME_MS = 5 * 60 * 1000;
const TEAM_RANK_HISTORY_GC_TIME_MS = 15 * 60 * 1000;

export const getTeamRankHistoryQueryKey = (teamId: string | undefined) =>
  [...TEAM_RANK_HISTORY_QUERY_KEY_PREFIX, teamId ?? ''] as const;

export type UseTeamRankHistoryOptions = Omit<
  UseQueryOptions<TeamRankHistoryResponse, Error, BETeamRankHistoryGameweek[]>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useTeamRankHistory = (
  teamId: string | undefined,
  options?: UseTeamRankHistoryOptions
): UseQueryResult<BETeamRankHistoryGameweek[], Error> => {
  const { enabled = true, ...restOptions } = options ?? {};

  return useQuery<TeamRankHistoryResponse, Error, BETeamRankHistoryGameweek[]>({
    queryKey: getTeamRankHistoryQueryKey(teamId),
    queryFn: async () => {
      if (!teamId) {
        throw new Error('teamId is required to fetch team rank history');
      }

      return fetchTeamRankHistory(teamId);
    },
    select: response => response.data.items,
    enabled: enabled && !!teamId,
    staleTime: TEAM_RANK_HISTORY_STALE_TIME_MS,
    gcTime: TEAM_RANK_HISTORY_GC_TIME_MS,
    ...restOptions,
  });
};
